//TODO : Refactor after ai
import type { HTMLAttributes } from "react";
import type { OverviewObject } from "./types";
import { CalendarEvent } from "./primitives";

const DOW = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

interface Cell {
  n: number;
  date: string;
  muted?: boolean;
}

export interface ObjectCalendarProps extends HTMLAttributes<HTMLDivElement> {
  items?: OverviewObject[];
  month?: number;
  year?: number;
  onOpen?: (item: OverviewObject) => void;
  onMonthChange?: (month: number, year: number) => void;
  maxPerDay?: number;
  dowLabels?: string[];
}

/** Calendar representation: objects placed on a month grid by their date. */
export function ObjectCalendar({
  items = [],
  month,
  year,
  onOpen,
  onMonthChange,
  maxPerDay = 3,
  dowLabels = DOW,
  className = "",
  ...rest
}: ObjectCalendarProps) {
  const today = new Date();
  const m = month ?? today.getMonth();
  const y = year ?? today.getFullYear();
  const first = new Date(y, m, 1);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const prevDays = new Date(y, m, 0).getDate();

  const byDate = new Map<string, OverviewObject[]>();
  items
    .filter((i) => i.date)
    .forEach((i) => {
      const key = i.date as string;
      byDate.set(key, [...(byDate.get(key) ?? []), i]);
    });

  const cells: Cell[] = [];
  for (let i = lead - 1; i >= 0; i--) {
    cells.push({ n: prevDays - i, muted: true, date: iso(new Date(y, m - 1, prevDays - i)) });
  }
  for (let d = 1; d <= days; d++) {
    cells.push({ n: d, date: iso(new Date(y, m, d)) });
  }
  let tail = 1;
  while (cells.length % 7) {
    cells.push({ n: tail, muted: true, date: iso(new Date(y, m + 1, tail)) });
    tail++;
  }

  const navButton = {
    width: 32,
    height: 32,
    display: "grid",
    placeItems: "center",
    border: 0,
    borderRadius: "var(--r-xs)",
    background: "transparent",
    cursor: "pointer",
    color: "var(--icon-tertiary)",
  } as const;

  return (
    <div className={className} {...rest}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <button
          type="button"
          aria-label="Vorheriger Monat"
          onClick={() => onMonthChange?.(m - 1, y)}
          style={navButton}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 6-6 6 6 6" />
          </svg>
        </button>
        <span
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 600,
            fontSize: 16,
            lineHeight: "24px",
            color: "var(--text-primary)",
            minWidth: 160,
          }}
        >
          {MONTHS[((m % 12) + 12) % 12]} {y}
        </span>
        <button
          type="button"
          aria-label="Nächster Monat"
          onClick={() => onMonthChange?.(m + 1, y)}
          style={navButton}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 6 6 6-6 6" />
          </svg>
        </button>
      </div>
      <div className="bui-ov-surface">
        <div className="bui-ov-cal">
          {dowLabels.map((d) => (
            <span key={d} className="bui-ov-cal__dow">
              {d}
            </span>
          ))}
          {cells.map((cell) => {
            const events = byDate.get(cell.date) ?? [];
            const isToday = cell.date === iso(today);
            return (
              <div
                key={cell.date}
                className="bui-ov-cal__cell"
                data-muted={cell.muted || undefined}
                data-today={isToday || undefined}
              >
                <span className="bui-ov-cal__num">{cell.n}</span>
                {events.slice(0, maxPerDay).map((event) => (
                  <CalendarEvent
                    key={event.id}
                    variant="dot"
                    color={event.statusColor ?? "brand"}
                    title={event.title}
                    time={event.time}
                    onClick={() => onOpen?.(event)}
                    style={{ cursor: onOpen ? "pointer" : "default" }}
                  />
                ))}
                {events.length > maxPerDay && (
                  <span className="bui-ov-cal__more">
                    +{events.length - maxPerDay} weitere
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
